import React from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { CheckCircle, Error } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { Loader } from '../../../Loader';
import { useDebouncedValue } from '../../../Inputs';
import { calendarVerifyICS } from '@uplift/api';


const useStyles = makeStyles()(theme => ({
  successIcon: {
    color: theme.palette.success.main,
  },
  errorIcon: {
    color: theme.palette.error.main,
  }
}));


export const ICSUrlVerification: React.VFC<{ url: string; onStatusChange: (valid: null | boolean) => void }> = ({ url: _url, onStatusChange }) => {
  const { classes } = useStyles();
  const [ url, isDebounceLoading ] = useDebouncedValue(_url, 500);
  const { data: verification, isLoading } = useQuery([ 'calendar-verification', url ], async () => {
    if (!url) {
      return null;
    }

    try {
      const response = await calendarVerifyICS(url);

      return response.data;
    } catch (err) {
      return { valid: false } as const;
    }
  });

  React.useEffect(() => {
    onStatusChange(verification?.valid ?? null);
  }, [ verification?.valid ]);

  if (isLoading || (_url && isDebounceLoading)) {
    return (
      <Box display="flex" alignItems="center" gap="10px">
        <Loader width="24px" height="24px" size="24px" color="primary" />
        <Typography>Looking for your calendar...</Typography>
      </Box>
    );
  }

  if (verification?.valid) {
    return (
      <Box display="flex" alignItems="center" gap="10px">
        <CheckCircle fontSize="medium" className={classes.successIcon} />
        <Typography>Verified calendar “<strong>{verification.title}</strong>”</Typography>
      </Box>
    );
  } else if (verification?.valid === false) {
    return (
      <Box display="flex" alignItems="center" gap="10px">
        <Error fontSize="medium" className={classes.errorIcon} />
        <Typography>Couldn’t find this calendar—check to make sure it’s the right URL.</Typography>
      </Box>
    );
  }

  return null;
};
